import { Link } from "react-router-dom";
import { useSite, usePageMeta } from "@/hooks/useSite";
import { orgJsonLd, summitEventJsonLd } from "@/lib/seo";
import { SiteLayout } from "@/components/SiteLayout";
import { Picture } from "@/components/Picture";
import { ChainNetworkDiagram } from "@/components/ChainNetworkDiagram";
import { SummitSection } from "@/components/SummitSection";
import { JoinForm } from "@/components/JoinForm";

const GPS = [
  { key: "growing", word: "Growing", text: "Farmers, seed keepers and cooperatives, and the soil, water and forests they look after." },
  { key: "preparing", word: "Preparing", text: "Cooks, kitchens, processors and food entrepreneurs, and the knowledge held in Nepal's many food cultures." },
  { key: "sharing", word: "Sharing", text: "Markets, schools, homes and festivals: the tables where food becomes community." },
];

const PRINCIPLES = [
  ["We connect before we build", "Many answers already exist somewhere in Nepal. We find them and link them."],
  ["We go where food is", "Conversations happen on farms, in kitchens and in community homestays, not only in meeting halls."],
  ["We work with others, not over them", "Everything we do is shaped together with partners."],
  ["What we learn is open", "Our knowledge and content are shared under a Creative Commons licence, free for anyone to use."],
];

const Hero = () => (
  <section className="hero" data-testid="hero-section">
    <div className="wrap">
      <div className="hero-text">
        <h1>From food chains to food networks.</h1>
        <p className="deva-line" lang="ne">खाद्य शृङ्खलाबाट खाद्य सञ्जालतर्फ</p>
        <p className="lede">We connect the people who grow, prepare and share food in Nepal, so that the system that feeds us becomes fairer, stronger and more our own.</p>
        <div className="actions">
          <a className="btn" href="#join" data-testid="hero-join-button">Join the network</a>
          <Link className="btn ghost" to="/our-story" data-testid="hero-story-button">Read our story</Link>
        </div>
      </div>
      <div className="hero-art">
        <Picture src="/assets/img/illustration.jpg" alt="Watercolour illustration: a woman carries a large fishing net over a river full of fish, lotus and vegetables, while a farmer bends to harvest beside her." width={1569} height={1600} fetchPriority="high" />
        <span className="credit">Illustration: Ubahang Nembang</span>
      </div>
    </div>
  </section>
);

const Idea = () => (
  <section className="section idea" id="idea">
    <div className="wrap">
      <div>
        <h2>Food in Nepal moves through chains. We think it should move through networks.</h2>
        <p>In a chain, each link knows only the next one. The farmer rarely meets the cook. The cook rarely meets the people who eat. And when one link breaks, everything after it stops, as the lockdowns showed us.</p>
        <p>In a network, people are connected in many directions. When one road closes, another opens. Knowledge travels, and value is shared more fairly.</p>
        <p>Nepal Food Networks is a not-for-profit company that weaves these connections, across districts, sectors and generations.</p>
      </div>
      <ChainNetworkDiagram />
    </div>
  </section>
);

const Gps = ({ photos }) => (
  <section className="section gps" aria-labelledby="gps-title">
    <h2 id="gps-title" className="visually-hidden">Growing, preparing, sharing</h2>
    <div className="wrap">
      {GPS.map((g) => (
        <div className="gps-row" key={g.key}>
          <div>
            <p className="gps-word">{g.word}</p>
            <p className="lede">{g.text}</p>
          </div>
          <div className="gps-photo">
            <Picture src={photos[g.key].url} alt={photos[g.key].alt} lazy data-testid={`gps-photo-${g.key}`} />
          </div>
        </div>
      ))}
      <p className="gps-close">Our work happens where these three meet.</p>
    </div>
  </section>
);

const HowWeWork = () => (
  <section className="section dark net-pattern" aria-labelledby="how-title">
    <div className="wrap">
      <h2 id="how-title">How we work</h2>
      <div className="principles">
        {PRINCIPLES.map(([h, p]) => (
          <div key={h}>
            <h3>{h}</h3>
            <p>{p}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Been = ({ photo, facts }) => (
  <section className="section been" aria-labelledby="been-title">
    <div className="wrap">
      <figure>
        <Picture src={photo.url} alt={photo.alt} lazy />
        {photo.caption && <figcaption className="credit">{photo.caption}</figcaption>}
      </figure>
      <div>
        <h2 id="been-title">Where we have been</h2>
        <p>Our team came together in 2019 to plan Nepal Food, a national fair and summit for 2020. We registered Nepal Food Networks in 2020, just as the pandemic arrived. The fair went on hold, so we did what the moment asked of us.</p>
        <ul className="facts" data-testid="facts-list">
          {facts.map((f) => (
            <li key={f.id}><strong>{f.number}</strong><span>{f.text}</span></li>
          ))}
        </ul>
        <Link className="btn ghost" to="/our-story" data-testid="been-story-button">Read our story</Link>
      </div>
    </div>
  </section>
);

const Join = () => (
  <section className="section join" id="join" aria-labelledby="join-title">
    <div className="wrap">
      <div>
        <h2 id="join-title">We are weaving again.</h2>
        <p>After a pause, Nepal Food Networks is starting again, slowly and with care. We are rebuilding with new people and a simple aim: to connect those already doing good work with food in Nepal, and see what grows from there.</p>
        <p>If you grow, cook, sell, teach, research or fund food in Nepal, or you simply care about it, we would like to know you. And if you want to help build Nepal Food Networks itself, tell us in your message.</p>
      </div>
      <JoinForm />
    </div>
  </section>
);

const buildHomeJsonLd = (data) => {
  if (!data) return [];
  const ld = [orgJsonLd(data.settings)];
  if (data.summit?.published) ld.push(summitEventJsonLd(data.summit));
  return ld;
};

export default function Home() {
  const { data } = useSite();
  usePageMeta({
    title: "Nepal Food Networks – From food chains to food networks",
    description: "Nepal Food Networks is a not-for-profit company that connects the people who grow, prepare and share food in Nepal.",
    path: "/",
    jsonLd: buildHomeJsonLd(data),
    verification: data?.settings?.verification,
  });
  if (!data) return null;
  return (
    <SiteLayout data={data} current="home">
      <Hero />
      <Idea />
      {data.sectionPhotos && <Gps photos={data.sectionPhotos} />}
      <HowWeWork />
      {data.sectionPhotos && <Been photo={data.sectionPhotos.been} facts={data.facts} />}
      {data.summit?.published && <SummitSection summit={data.summit} />}
      <Join />
    </SiteLayout>
  );
}
